/**
 * Frontend SecretsManager
 * ------------------------------------------------------------------
 * Single access point for the UI's environment configuration and the
 * (build-time) secrets that used to be read directly in `config.js`.
 *
 * Unlike the backend SecretsManager, the browser cannot call AWS Secrets
 * Manager at runtime (it has no AWS credentials), so the per-environment
 * `secrets.json` here is still resolved at build time. Centralising it
 * behind this module means:
 *   - `config.<env>.js` (production.js / qa.js / ...) and `secrets.json`
 *     are required in exactly ONE place, and
 *   - the resolution can later be swapped to fetch from a backend endpoint
 *     (which itself reads from AWS Secrets Manager) without touching every
 *     consumer.
 *
 * Only the bugsnag api key per service profile lives in the UI secrets, so
 * nothing sensitive beyond that is bundled.
 */
const { config } = require("../../package.json");

/**
 * Resolve the environment config (base env file + secrets) for a backend.
 * This is the single place that requires `config.<env>.js` and the matching
 * `secrets.json`.
 *
 * @param {string} backend - e.g. "prod" | "qa" | "prod-eu" | "dev-eu" | "dev"
 */
export const getEnvironment = (backend = config.backend) => {
    if (backend == "alt") {
        const production = require("./prod/production").default;
        const alt = require("./alt/alternate").default;
        return {
            ...production,
            AWS: {
                ...production.AWS,
                Auth: alt.AWS.Auth
            }
        };
    }

    let secrets;
    let env;

    if (backend === "prod") {
        secrets = require("./prod/secrets.json");
        env = require("./prod/production").default;
    } else if (backend === "qa") {
        secrets = require("./qa/secrets.json");
        env = require("./qa/qa").default;
    } else if (backend === "prod-eu") {
        secrets = require("./prod-eu/secrets.json");
        env = require("./prod-eu/prod-eu").default;
    } else if (backend === "dev-eu") {
        secrets = require("./dev-eu/secrets.json");
        env = require("./dev-eu/dev-eu").default;
    } else {
        secrets = require("./dev/secrets.json");
        env = require("./dev/development").default;
    }

    // Narrow the secrets to the active service profile and merge onto env.
    secrets = secrets.profiles[config.serviceProfile];

    return Object.assign(env, secrets);
};

/**
 * For region-switching flows, resolve the config of the "paired" region.
 */
export const getTargetRegionConfig = (currentBackend) => {
    // Determine target backend based on region switching logic
    let targetBackend;

    if (currentBackend === "prod") {
        targetBackend = "prod-eu";
    } else if (currentBackend === "prod-eu") {
        targetBackend = "prod";
    } else if (currentBackend === "qa") {
        targetBackend = "dev-eu";
    } else if (currentBackend === "dev-eu") {
        targetBackend = "qa";
    } else {
        // For local development or other cases, return current config
        return getEnvironment(currentBackend);
    }

    // Use the parameterized getEnvironment with target backend
    return getEnvironment(targetBackend);
};

export default { getEnvironment, getTargetRegionConfig };
